import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import Link from '@mui/icons-material/Link';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import ToggleOnOutlined from '@mui/icons-material/ToggleOnOutlined';
import WarningAmber from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { TimeFormat } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useMemo } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import { matchKeysColumn } from '~components/MatchKeys/matchKeysColumn';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { MatchKey } from '~hooks/useMatchKeys/models';
import { Site } from '~hooks/useSites/models';
import { formatSiteHours } from '~pages/Settings/Administration/Sites/site-form-v2/siteTimeZoneHints';
import { Pagination, PaginationLink } from '~services/pagination';
import { dateFormat } from '~utils/dateTime';

interface UserPermissions {
  canCreateSite: boolean;
  canEditSite: boolean;
  canDeleteSite: boolean;
}

interface SitesTableViewProps {
  sites: Site[];
  isLoading: boolean;
  matchKeysByMatchableId: Map<string, MatchKey[]> | null;
  showMatchKeys: boolean;
  pagination: Pagination;
  timeFormat: TimeFormat;
  userPermissions: UserPermissions;
  onEditSite: (siteId: string) => void;
  onDeleteSite: (siteId: string) => void;
  onToggleSiteStatus: (siteId: string, isActive: boolean) => void;
  onDeleteCompanyShare: (companyShareId: string) => void;
  onPaginationChange: (link?: PaginationLink) => void;
}

export function SitesTableView({
  sites,
  isLoading,
  matchKeysByMatchableId,
  showMatchKeys,
  pagination,
  timeFormat,
  userPermissions,
  onEditSite,
  onDeleteSite,
  onToggleSiteStatus,
  onDeleteCompanyShare,
  onPaginationChange,
}: SitesTableViewProps) {
  const theme = useTheme();

  const columns = useMemo<Array<DataTableColumn<Site>>>(
    () => [
      {
        field: 'name',
        headerName: t('form_fields.name'),
        valueGetter: (site) => site.name,
      },
      {
        field: 'externalId',
        headerName: t('form_fields.id'),
        valueGetter: (site) => site.externalId,
      },
      {
        field: 'fullAddress',
        headerName: t('form_fields.address'),
        valueGetter: (site) => site.fullAddress,
      },
      {
        field: 'latLong',
        headerName: t('form_fields.lat_lon_column_heading'),
        valueGetter: (site) => site.latLon,
      },
      {
        field: 'companyName',
        headerName: t('form_fields.company'),
        valueGetter: (site) => site.companyName,
      },
      {
        field: 'siteType',
        headerName: t('form_fields.site_type'),
        valueGetter: (site) => site.siteType,
      },
      {
        field: 'department',
        headerName: t('form_fields.department'),
        valueGetter: (site) => site.department?.name ?? '',
      },
      {
        field: 'hours',
        headerName: t('administration.site.hours'),
        valueGetter: (site) => formatSiteHours(site.openAt, site.closeAt, timeFormat),
      },
      ...(showMatchKeys ? [matchKeysColumn<Site>(matchKeysByMatchableId)] : []),
      {
        field: 'createdAt',
        headerName: t('common.created_on'),
        renderCell: (site) => {
          if (!site.createdAt) {
            return null;
          }

          const formatted = dateFormat(site.createdAt);
          return (
            <Tooltip title={`${t('common.created_on')}: ${formatted}`}>
              <Typography component="span" sx={{ fontSize: '12px' }}>
                {formatted}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'updatedAt',
        headerName: t('common.last_update'),
        renderCell: (site) => {
          if (!site.updatedAt) {
            return null;
          }

          const formatted = dateFormat(site.updatedAt);
          return (
            <Tooltip title={`${t('common.last_update')}: ${formatted}`}>
              <Typography component="span" sx={{ fontSize: '12px' }}>
                {formatted}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: 'isActive',
        headerName: t('form_fields.status'),
        renderCell: (site) => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              color={site.isActive ? 'success' : 'default'}
              label={site.isActive ? t('common.active') : t('common.inactive')}
              variant={'outlined'}
            />
            {site.companyShare && (
              <Link sx={{ color: theme.brandV2.colors.treadGreen, fontSize: '20px' }} />
            )}
          </Box>
        ),
      },
      {
        field: 'geofenceType',
        headerName: t('form_fields.geo_fence_type'),
        renderCell: (site) => {
          const { nextBillionGeofence } = site;
          if (!nextBillionGeofence) {
            return null;
          }

          const nextBillionGeofenceType = nextBillionGeofence.movingGeofence
            ? 'Equipment'
            : nextBillionGeofence.type;
          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant={'body1'} sx={{ textTransform: 'capitalize' }}>
                {nextBillionGeofenceType}
              </Typography>
            </Box>
          );
        },
      },
      {
        defaultHidden: true,
        field: 'notes',
        headerName: t('form_fields.notes'),
        valueGetter: (site) => site.notes,
      },
      {
        field: 'alerts',
        headerName: t('actions.alerts'),
        hideable: false,
        renderCell: (site) =>
          !site.routable && (
            <Box>
              <Tooltip title={t('administration.site.address_confirmation_required')}>
                <WarningAmber color="primary" />
              </Tooltip>
            </Box>
          ),
      },
      actionsColumn((site) => {
        const { id, isActive, companyShare } = site;

        const options = [];

        if (userPermissions.canDeleteSite) {
          if (companyShare) {
            options.push({
              title: t('actions.remove_share'),
              icon: <ToggleOffOutlined />,
              callBack: () => {
                if (companyShare.id) {
                  onDeleteCompanyShare(companyShare.id);
                }
              },
            });
          } else {
            options.push({
              title: t('actions.delete'),
              icon: <Delete />,
              callBack: () => onDeleteSite(id),
            });
          }
        }

        if (userPermissions.canEditSite && !companyShare) {
          options.push({
            title: isActive ? t('actions.deactivate') : t('actions.activate'),
            icon: isActive ? (
              <ToggleOffOutlined />
            ) : (
              <ToggleOnOutlined color={'primary'} />
            ),
            callBack: () => onToggleSiteStatus(id, isActive),
          });
        }

        return (
          <>
            {userPermissions.canEditSite && (
              <IconButton
                aria-label={t('actions.edit')}
                size="small"
                onClick={() => onEditSite(id)}
              >
                <Edit />
              </IconButton>
            )}
            <SimpleMenu options={options}>
              <MoreHoriz />
            </SimpleMenu>
          </>
        );
      }, 'extra.actions'),
    ],
    [
      matchKeysByMatchableId,
      showMatchKeys,
      userPermissions,
      onDeleteCompanyShare,
      onDeleteSite,
      onEditSite,
      onToggleSiteStatus,
      theme.brandV2.colors.treadGreen,
      timeFormat,
    ],
  );

  return (
    <DataTable
      id="company-assets-sites"
      columns={columns}
      rows={sites}
      loading={isLoading}
      headerTrailingComponent={
        <DataGridPager
          isFetching={isLoading}
          onNavigate={onPaginationChange}
          pagination={pagination}
        />
      }
    />
  );
}
